import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Eye, XCircle, CheckCircle, AlertCircle, Loader,
  RotateCcw, FileText,
  Banknote, Smartphone, CreditCard, FileCheck, Download, RotateCcw as ReturnIcon,
  Search, Receipt, FileDown, X, Edit2, Gift
} from 'lucide-react';
import { getVentes, annulerVente, getFacturePDF, getTicketPDF } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useBoutique } from '../context/BoutiqueContext';
import BoutiqueSelector from '../components/BoutiqueSelector';
import './Ventes.css';

const fmtCur  = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0)) + ' XOF';
const fmtDate = (d) => d ? new Date(d).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—';
// Période filtrée conservée dans le navigateur (survit au rafraîchissement)
// jusqu'à « Réinitialiser ». Accès protégés : stockage indisponible = pas de mémoire.
const CLE_DATE_DEBUT = 'ventes_filtre_date_debut';
const CLE_DATE_FIN   = 'ventes_filtre_date_fin';
const lireDate = (cle) => {
  try { return localStorage.getItem(cle) || ''; } catch { return ''; }
};
const ecrireDate = (cle, val) => {
  try { val ? localStorage.setItem(cle, val) : localStorage.removeItem(cle); } catch { /* ignoré */ }
};

const clientNom = (v) => v?.clientId?.nom || v?.client?.nom || '';
const clientTel = (v) => v?.clientId?.telephone || v?.client?.telephone || '';

// Texte normalisé pour la recherche : minuscules, sans accents
const normaliser = (s) => String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// Tout le texte recherchable d'une vente : n° facture, vendeur, client, commentaire,
// et pour chaque produit vendu : nom, code, code-barres, description, champs personnalisés.
const texteRecherche = (v) => {
  const parts = [
    v.numeroFacture,
    v.vendeur?.prenom, v.vendeur?.nom,
    clientNom(v), clientTel(v),
    v.commentaire,
  ];
  for (const d of v.details || []) {
    const p = d.produit && typeof d.produit === 'object' ? d.produit : {};
    parts.push(d.nomProduit, d.codeProduit, d.codeBarres, p.nom, p.codeProduit, p.codeBarres, p.description);
    for (const c of p.champsPersonnalises || []) parts.push(c.cle, c.valeur);
  }
  return normaliser(parts.filter(Boolean).join(' '));
};

const MODES = {
  especes: { label: 'Espèces',      icon: Banknote,    color:'#16a34a', bg:'#dcfce7' },
  wave:    { label: 'Wave',         icon: Smartphone,  color:'#6366f1', bg:'#eef2ff' },
  orange_money: { label: 'Orange Money', icon: Smartphone, color:'#f59e0b', bg:'#fffbeb' },
  carte_bancaire: { label: 'Carte',  icon: CreditCard,  color:'#3b82f6', bg:'#eff6ff' },
  cheque:  { label: 'Chèque',       icon: FileCheck,   color:'#8b5cf6', bg:'#f5f3ff' },
  autre:   { label: 'Autre',        icon: FileText,    color:'#64748b', bg:'#f1f5f9' },
};

const STATUTS = {
  payee:           { label: 'Payée',          cls: 'vn-statut--payee' },
  annulee:         { label: 'Annulée',        cls: 'vn-statut--annulee' },
  dette_partielle: { label: 'Dette partielle', cls: 'vn-statut--dette-p' },
  dette_complete:  { label: 'Dette complète',  cls: 'vn-statut--dette-c' },
  en_attente:      { label: 'En attente',     cls: 'vn-statut--attente' },
};

const PAGE_SIZE = 25;

export default function Ventes() {
  const navigate = useNavigate();
  const { user }  = useAuth();
  const { selectedBoutiqueId } = useBoutique();

  const [ventes,     setVentes]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [toast,      setToast]      = useState(null);
  const [pdfLoading, setPdfLoading] = useState({});
  const [ticketLoading, setTicketLoading] = useState({});

  const [search,      setSearch]      = useState('');
  const [filterMode,  setFilterMode]  = useState('');
  const [filterStatut,setFilterStatut]= useState('');
  const [dateDebut,   setDateDebut]   = useState(() => lireDate(CLE_DATE_DEBUT));
  const [dateFin,     setDateFin]     = useState(() => lireDate(CLE_DATE_FIN));
  useEffect(() => { ecrireDate(CLE_DATE_DEBUT, dateDebut); }, [dateDebut]);
  useEffect(() => { ecrireDate(CLE_DATE_FIN, dateFin); }, [dateFin]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const isAdmin = ['admin-entreprise', 'admin-pro', 'super-admin'].includes(user?.role);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => { loadVentes(); }, [selectedBoutiqueId]); // eslint-disable-line

  const loadVentes = async () => {
    setLoading(true);
    try {
      const { data } = await getVentes();
      setVentes(data.data || []);
    } catch { showToast('error', 'Erreur lors du chargement'); }
    finally { setLoading(false); }
  };

  // Index de recherche : calculé une fois par chargement des ventes
  const indexRecherche = useMemo(() => {
    const m = new Map();
    for (const v of ventes) m.set(v._id, texteRecherche(v));
    return m;
  }, [ventes]);

  const filtered = useMemo(() => {
    let r = ventes;
    if (selectedBoutiqueId && selectedBoutiqueId !== 'all')
      r = r.filter(v => v.boutiqueId === selectedBoutiqueId);
    // Tous les mots saisis doivent être présents (dans n'importe quel champ indexé)
    const mots = normaliser(search).split(/\s+/).filter(Boolean);
    if (mots.length) {
      r = r.filter(v => {
        const txt = indexRecherche.get(v._id) || '';
        return mots.every(m => txt.includes(m));
      });
    }
    if (filterMode)   r = r.filter(v => v.modePaiement === filterMode);
    if (filterStatut) r = r.filter(v => v.statut === filterStatut);
    if (dateDebut)    r = r.filter(v => new Date(v.createdAt) >= new Date(dateDebut));
    if (dateFin)      r = r.filter(v => new Date(v.createdAt) <= new Date(dateFin + 'T23:59:59'));
    return r;
  }, [ventes, indexRecherche, selectedBoutiqueId, search, filterMode, filterStatut, dateDebut, dateFin]);

  // CA = valeur facturée, toutes ventes non annulées (y compris les ventes à
  // crédit, dont l'acompte fait partie du chiffre d'affaires).
  // Encaissé = argent réellement reçu — c'est cette valeur qui doit correspondre
  // à la caisse, qui compte elle aussi les montants payés.
  const { totalCA, totalEncaisse } = useMemo(() => {
    const valides = filtered.filter(v => v.statut !== 'annulee' && v.statut !== 'en_attente');
    return {
      totalCA:       valides.reduce((s, v) => s + (v.totalTTC || 0), 0),
      totalEncaisse: valides.reduce((s, v) => s + (v.montantPaye || 0), 0),
    };
  }, [filtered]);

  const activeFiltersCount = [search, filterMode, filterStatut, dateDebut, dateFin].filter(Boolean).length;

  const resetFilters = () => {
    setSearch(''); setFilterMode(''); setFilterStatut(''); setDateDebut(''); setDateFin('');
    setVisibleCount(PAGE_SIZE);
  };

  const handleAnnuler = async (id, enAttente = false) => {
    const msg = enAttente
      ? 'Annuler cette vente en attente ?'
      : 'Annuler cette vente ? Le stock sera remis à jour.';
    if (!window.confirm(msg)) return;
    try {
      await annulerVente(id);
      showToast('success', 'Vente annulée avec succès');
      loadVentes();
    } catch (err) {
      showToast('error', err.response?.data?.message || "Erreur lors de l'annulation");
    }
  };

  const handlePDF = async (id) => {
    setPdfLoading(p => ({ ...p, [id]: true }));
    try {
      const res = await getFacturePDF(id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch { showToast('error', 'Erreur lors de la génération de la facture'); }
    finally { setPdfLoading(p => ({ ...p, [id]: false })); }
  };

  const handleTicket = async (id) => {
    setTicketLoading(p => ({ ...p, [id]: true }));
    try {
      const res = await getTicketPDF(id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch { showToast('error', 'Erreur lors de la génération du ticket'); }
    finally { setTicketLoading(p => ({ ...p, [id]: false })); }
  };

  const exportCSV = () => {
    if (!filtered.length) return;
    const headers = ['Numéro facture','Date','Vendeur','Client','Articles','Total TTC','Montant payé','Reste','Mode paiement','Statut'];
    const rows = filtered.map(v => [
      v.numeroFacture,
      new Date(v.createdAt).toISOString(),
      `${v.vendeur?.prenom || ''} ${v.vendeur?.nom || ''}`.trim(),
      clientNom(v),
      v.details?.length || 0,
      v.totalTTC || 0,
      v.montantPaye || 0,
      Math.max(0, (v.totalTTC || 0) - (v.montantPaye || 0)),
      MODES[v.modePaiement]?.label || v.modePaiement,
      STATUTS[v.statut]?.label || v.statut,
    ]);
    const escape = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map(r => r.map(escape).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ventes-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const visible = filtered.slice(0, visibleCount);

  if (loading) return (
    <div className="vn-loading"><Loader className="vn-spinner" size={28}/><span>Chargement des ventes…</span></div>
  );

  return (
    <div className="vn-page">
      {toast && (
        <div className={`vn-toast vn-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={13}/> : <AlertCircle size={13}/>}
          {toast.text}
        </div>
      )}

      {/* ── Header ── */}
      <div className="vn-header">
        <div className="vn-header-inner">
          <div className="vn-header-left">
            <div className="vn-header-icon"><Receipt size={22}/></div>
            <div>
              <h1 className="vn-header-title">Ventes</h1>
              <p className="vn-header-sub">
                {filtered.length} transaction{filtered.length > 1 ? 's' : ''} · CA {fmtCur(totalCA)}
                {totalEncaisse !== totalCA && (
                  <> · encaissé {fmtCur(totalEncaisse)}</>
                )}
              </p>
            </div>
          </div>
          <div className="vn-header-right">
            {isAdmin && <BoutiqueSelector/>}
            <button className="vn-secondary-btn" onClick={exportCSV} disabled={!filtered.length}>
              <FileDown size={14}/> Exporter
            </button>
            <button className="vn-new-btn" onClick={() => navigate('/ventes/nouvelle')}>
              <Plus size={15}/> Nouvelle vente
            </button>
          </div>
        </div>
      </div>

      <div className="vn-content">

        {/* ── Bloc unique : filtres + tableau ── */}
        <div className="vn-panel">

          {/* Filtres */}
          <div className="vn-filters-bar">
            <div className="vn-search-wrap">
              <Search size={14} className="vn-search-icon"/>
              <input className="vn-search-input" type="text"
                placeholder="Rechercher : n° facture, client, vendeur, produit, description, champs personnalisés…"
                value={search} onChange={e => setSearch(e.target.value)}/>
              {search && (
                <button className="vn-search-clear" onClick={() => setSearch('')} title="Effacer">
                  <X size={13}/>
                </button>
              )}
            </div>

            <select className="vn-select" value={filterMode} onChange={e => setFilterMode(e.target.value)} title="Mode de paiement">
              <option value="">Tous les modes</option>
              {Object.entries(MODES).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </select>

            <select className="vn-select" value={filterStatut} onChange={e => setFilterStatut(e.target.value)} title="Statut">
              <option value="">Tous les statuts</option>
              {Object.entries(STATUTS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
            </select>

            <input className="vn-date-input" type="date" value={dateDebut}
              onChange={e => setDateDebut(e.target.value)} title="Date début" placeholder="Début"/>

            <input className="vn-date-input" type="date" value={dateFin}
              onChange={e => setDateFin(e.target.value)} title="Date fin" placeholder="Fin"/>

            {activeFiltersCount > 0 && (
              <button className="vn-reset-btn" onClick={resetFilters} title="Réinitialiser les filtres">
                <RotateCcw size={13}/>
                <span>Réinitialiser</span>
              </button>
            )}
          </div>

          {/* Tableau */}
          {filtered.length === 0 ? (
            <div className="vn-empty">
              <div className="vn-empty-icon"><Receipt size={36}/></div>
              <h3>Aucune vente trouvée</h3>
              <p>{activeFiltersCount > 0 ? 'Modifiez les filtres ou réinitialisez pour voir toutes les ventes.' : 'Créez votre première vente pour démarrer.'}</p>
              {activeFiltersCount > 0
                ? <button className="vn-empty-cta" onClick={resetFilters}><RotateCcw size={14}/> Réinitialiser les filtres</button>
                : <button className="vn-empty-cta" onClick={() => navigate('/ventes/nouvelle')}><Plus size={14}/> Nouvelle vente</button>}
            </div>
          ) : (
            <>
              <div className="vn-table-wrap">
                <table className="vn-table">
                  <thead>
                    <tr>
                      <th>N° facture</th>
                      <th>Date</th>
                      <th>Vendeur</th>
                      <th>Client</th>
                      <th className="vn-th-right">Articles</th>
                      <th className="vn-th-right">Total</th>
                      <th>Mode</th>
                      <th>Statut</th>
                      <th className="vn-th-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map(v => {
                      const mode      = MODES[v.modePaiement] || MODES.autre;
                      const ModeIcon  = mode.icon;
                      const statut    = STATUTS[v.statut] || STATUTS.en_attente;
                      const isAnnulee = v.statut === 'annulee';
                      const isPending = v.statut === 'en_attente';
                      const canCancel = ['payee', 'dette_partielle', 'dette_complete'].includes(v.statut);
                      const reste     = Math.max(0, (v.totalTTC || 0) - (v.montantPaye || 0));
                      return (
                        <tr key={v._id} className={`${isAnnulee ? 'vn-row--annulee' : ''}${isPending ? ' vn-row--pending' : ''}`}>
                          <td className="vn-td-num">
                            {v.numeroFacture}
                            {v.loyaltyCoupon?.code && (
                              <span title={`Bon fidélité ${v.loyaltyCoupon.code}${v.loyaltyCoupon.label ? ` (${v.loyaltyCoupon.label})` : ''}`}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginLeft: 6,
                                  padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                                  background: '#eef2ff', color: '#4338ca', verticalAlign: 'middle' }}>
                                <Gift size={10}/> {v.loyaltyCoupon.label || 'Bon'}
                              </span>
                            )}
                          </td>
                          <td className="vn-td-date">{fmtDate(v.createdAt)}</td>
                          <td>
                            <div className="vn-td-vendeur">
                              <div className="vn-avatar">{(v.vendeur?.prenom?.[0] || 'U').toUpperCase()}</div>
                              <span>{v.vendeur?.prenom} {v.vendeur?.nom}</span>
                            </div>
                          </td>
                          <td>
                            {clientNom(v) ? (
                              <div className="vn-td-client">
                                <span className="vn-td-client-nom">{clientNom(v)}</span>
                                {clientTel(v) && <span className="vn-td-client-tel">{clientTel(v)}</span>}
                              </div>
                            ) : (
                              <span className="vn-muted">—</span>
                            )}
                          </td>
                          <td className="vn-td-right">{v.details?.length || 0}</td>
                          <td className="vn-td-right">
                            <div className={`vn-td-total ${isAnnulee ? 'vn-total--annulee' : ''}`}>
                              {fmtCur(v.totalTTC)}
                            </div>
                            {reste > 0 && !isAnnulee && (
                              <div className="vn-td-reste">Reste {fmtCur(reste)}</div>
                            )}
                          </td>
                          <td>
                            <span className="vn-mode-badge" style={{ background: mode.bg, color: mode.color }}>
                              <ModeIcon size={10}/> {mode.label}
                            </span>
                          </td>
                          <td>
                            <span className={`vn-statut-badge ${statut.cls}`}>{statut.label}</span>
                          </td>
                          <td>
                            <div className="vn-row-actions">
                              {isPending && (
                                <button className="vn-btn vn-btn--edit" title="Modifier / finaliser cette vente"
                                  onClick={() => navigate(`/ventes/nouvelle?id=${v._id}`)}>
                                  <Edit2 size={13}/>
                                </button>
                              )}
                              <button className="vn-btn vn-btn--view" title="Voir la facture"
                                onClick={() => navigate(`/factures?id=${v._id}`)}>
                                <Eye size={13}/>
                              </button>
                              <button
                                className="vn-btn vn-btn--pdf"
                                title="Télécharger la facture PDF"
                                disabled={!!pdfLoading[v._id]}
                                onClick={() => handlePDF(v._id)}>
                                {pdfLoading[v._id] ? <Loader size={13} className="vn-spin"/> : <Download size={13}/>}
                              </button>
                              <button
                                className="vn-btn vn-btn--ticket"
                                title="Imprimer le ticket de caisse"
                                disabled={!!ticketLoading[v._id]}
                                onClick={() => handleTicket(v._id)}>
                                {ticketLoading[v._id] ? <Loader size={13} className="vn-spin"/> : <Receipt size={13}/>}
                              </button>
                              {/* En attente : pas de retour possible (aucun stock sorti) → bouton « Annuler la vente » à la place */}
                              {!isAnnulee && !isPending && (
                                <button className="vn-btn vn-btn--return" title="Créer un retour"
                                  onClick={() => navigate(`/retours?vente=${v._id}`)}>
                                  <ReturnIcon size={13}/>
                                </button>
                              )}
                              {(isPending || (isAdmin && canCancel)) && (
                                <button className="vn-btn vn-btn--cancel" title="Annuler la vente"
                                  onClick={() => handleAnnuler(v._id, isPending)}>
                                  <XCircle size={13}/>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {visible.length < filtered.length && (
                <div className="vn-loadmore-wrap">
                  <button className="vn-loadmore" onClick={() => setVisibleCount(c => c + PAGE_SIZE)}>
                    Charger {Math.min(PAGE_SIZE, filtered.length - visible.length)} ventes supplémentaires
                    <span className="vn-loadmore-count">{visible.length} / {filtered.length}</span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
